import React, { useEffect, useState } from "react";
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Card,
    Chip,
    Grid,
    Typography,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TablePagination,
    Box,
} from "@mui/material";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LanguageIcon from "@mui/icons-material/Language";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import BusinessIcon from "@mui/icons-material/Business";
import WorkIcon from "@mui/icons-material/Work";
import { EDIT_ICON, TRASH_ICON } from "../../assets/constants/Icons";
import ShowToast from "components/ShowToast";
import { TYPE_SUCCESS, TYPE_FAIL, INTERVAL } from "assets/constants/Constants";
import Spinner from "components/Spinner";
import "./EmployerMandateList.css";
import { getEmployerMandateList, deleteEmployerMandate, setAcceptedRejectedFlagMandate } from "lib/axiosUtils";

const EmployerMandateList = () => {
    const [mandateData, setMandateData] = useState([]);
    const [loadingComplete, setLoadingComplete] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);

    useEffect(() => {
        fetchEmployerMandateData();
    }, []);

    const fetchEmployerMandateData = async () => {
        try {
            const data = await getEmployerMandateList({}, {});
            setMandateData(data.data.result || []);
            setLoadingComplete(true);
        } catch (error) {
            console.log("Error while fetching employer mandate data", error);
            setLoadingComplete(true);
        }
    };

    const handleChangePage = (_event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleEditMandate = (mandate) => {
        window.open(`${process.env.REACT_APP_TJPLOGIN_URL}/employers/submit-entry-level-requirement.html?id=${mandate._id}`, '_blank');
    };

    const handleDeleteMandate = async (mandate) => {
        if (window.confirm("Are you sure you want to delete this Mandate?")) {
            try {
                const deleteResponse = await deleteEmployerMandate({}, {}, mandate._id);
                if (deleteResponse.statusCode === 200) {
                    await fetchEmployerMandateData();
                    ShowToast('Employer Mandate deleted successfully', TYPE_SUCCESS, INTERVAL, "", "");
                } else {
                    ShowToast('Something went wrong. Please delete again after sometime!', TYPE_FAIL, INTERVAL, "", "");
                }
            } catch (error) {
                console.log("Error while deleting employer mandate", error);
            }
        }
    }

    const handleUpdateMandateStatus = async (mandate, isAccepted) => {
        if (window.confirm(`Are you sure you want to ${isAccepted ? "accept" : "reject"} this Mandate?`)) {
            try {
                const body = { is_accepted: isAccepted };
                const updateResponse = await setAcceptedRejectedFlagMandate({}, body, mandate._id);
                if (updateResponse.statusCode === 200) {
                    await fetchEmployerMandateData();
                    ShowToast(
                        `Employer mandate ${isAccepted ? "accepted" : "rejected"} successfully`,
                        TYPE_SUCCESS,
                        INTERVAL,
                        "",
                        ""
                    );
                } else {
                    ShowToast('Something went wrong. Please try again.', TYPE_FAIL, INTERVAL, "", "");
                }
            } catch (error) {
                console.log("Error while updating employer mandate", error);
                ShowToast('Something went wrong. Please try again.', TYPE_FAIL, INTERVAL, "", "");
            }
        }
    }

    const paginatedMandateData = mandateData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    return (
        <>
            {
                loadingComplete === false ? (
                    <Spinner />
                ) : (
                    <div className="main-content">

                        <div
                            className="pagetitle row pagetitle-style"
                        >
                            Employer Mandate
                        </div>

                        {
                            mandateData?.length === 0 ? (
                                <Card
                                    className="no-data-card-style"
                                >
                                    No Data Found
                                </Card>
                            ) : (
                                paginatedMandateData.map((item, index) => (

                                    <Accordion
                                        key={index}
                                        className="employer-mandate-accordion"
                                    >

                                        {/* HEADER */}
                                        <AccordionSummary
                                            expandIcon={<ExpandMoreIcon />}
                                            className="employer-mandate-accordion-summary"
                                        >

                                            <Grid
                                                container
                                                spacing={2}
                                                alignItems="center"
                                            >

                                                {/* LEFT */}
                                                <Grid item xs={12} md={3}>
                                                    <Box
                                                        className="employer-info-box"
                                                    >
                                                        <Box>
                                                            <Typography
                                                                className="employer-organisation-name"
                                                            >
                                                                {item.organisation_name}
                                                            </Typography>

                                                            <div
                                                                className="employer-website"
                                                            >
                                                                <LanguageIcon />
                                                                <Typography>
                                                                    {item.website || '-'}
                                                                </Typography>
                                                            </div>
                                                        </Box>
                                                    </Box>
                                                </Grid>

                                                {/* CENTER */}
                                                <Grid item xs={12} md={3}>
                                                    <Grid container spacing={1}>
                                                        <Grid item xs={12}>
                                                            <Grid container alignItems="center" spacing={1}>
                                                                <Grid item>
                                                                    <PersonIcon
                                                                        className="contact-person-icon"
                                                                    />
                                                                </Grid>
                                                                <Grid item>
                                                                    <Typography
                                                                        className="contact-person-name"
                                                                    >
                                                                        {item.contact_name}
                                                                    </Typography>
                                                                </Grid>
                                                                <Grid item>
                                                                    <Chip
                                                                        label={
                                                                            item.contact_designation || "-"
                                                                        }
                                                                        size="small"
                                                                    />
                                                                </Grid>
                                                            </Grid>
                                                        </Grid>

                                                        <Grid item xs={12}>
                                                            <Grid container alignItems="center" spacing={1}>
                                                                <Grid item>
                                                                    <PhoneIcon
                                                                        className="contact-person-icon"
                                                                    />
                                                                </Grid>
                                                                <Grid item>
                                                                    <Typography>
                                                                        {item.contact_phone}
                                                                    </Typography>
                                                                </Grid>
                                                            </Grid>
                                                        </Grid>
                                                    </Grid>
                                                </Grid>

                                                {/* SUBMITTED */}
                                                <Grid item xs={12} md={2}>
                                                    <Box
                                                        className="submitted-info-box">
                                                        <Typography
                                                            className="submitted-info-label"
                                                        >
                                                            Submitted at:
                                                        </Typography>
                                                        <Typography
                                                            className="submitted-info-value"
                                                        >
                                                            {item.created_date_time ? new Date(item.created_date_time).toLocaleString('en-IN', {
                                                                day: '2-digit',
                                                                month: '2-digit',
                                                                year: 'numeric',
                                                                hour: '2-digit',
                                                                minute: '2-digit',
                                                            }) : '-'}
                                                        </Typography>
                                                    </Box>
                                                </Grid>

                                                {/* RIGHT */}
                                                <Grid item xs={12} md={4}>
                                                    <Box
                                                        className="action-buttons-box"
                                                    >
                                                        {item.is_accepted === true ? (
                                                            <button
                                                                type="button"
                                                                className="brand-button small-button employer-action-button"
                                                                style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}
                                                                onClick={(e) => e.stopPropagation()}
                                                            >
                                                                <i className="fa fa-check" aria-hidden="true" />
                                                                Approved
                                                            </button>
                                                        ) : item.is_accepted === false ? (
                                                            <button
                                                                type="button"
                                                                className="cancel_button small-button employer-action-button"
                                                                style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}
                                                                onClick={(e) => e.stopPropagation()}
                                                            >
                                                                <i className="fa fa-times" aria-hidden="true" />
                                                                Rejected
                                                            </button>
                                                        ) : (
                                                            <>
                                                                <button
                                                                    type="button"
                                                                    className="brand-button small-button employer-action-button"
                                                                    style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}
                                                                    onClick={(e) => { e.stopPropagation(); handleUpdateMandateStatus(item, true); }}
                                                                >
                                                                    <i className="fa fa-check" aria-hidden="true" />
                                                                    Approve
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className="cancel_button small-button employer-action-button"
                                                                    style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}
                                                                    onClick={(e) => { e.stopPropagation(); handleUpdateMandateStatus(item, false); }}
                                                                >
                                                                    <i className="fa fa-times" aria-hidden="true" />
                                                                    Reject
                                                                </button>
                                                            </>
                                                        )}
                                                        <button
                                                            type="button"
                                                            className="brand-button small-button employer-action-button icon-only"
                                                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
                                                            onClick={(e) => { e.stopPropagation(); handleEditMandate(item); }}
                                                        >
                                                            <i className={EDIT_ICON} aria-hidden="true" title="Edit Mandate" />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="brand-button small-button employer-action-button icon-only"
                                                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
                                                            onClick={(e) => { e.stopPropagation(); handleDeleteMandate(item); }}
                                                        >
                                                            <i className={TRASH_ICON} aria-hidden="true" title="Delete Mandate" />
                                                        </button>
                                                    </Box>
                                                </Grid>
                                            </Grid>
                                        </AccordionSummary>

                                        {/* DETAILS */}
                                        <AccordionDetails
                                            style={{
                                                background: "#fafafa",
                                                padding: 24,
                                            }}
                                        >
                                            {/* REQUIREMENTS */}
                                            <Divider
                                                style={{
                                                    marginBottom: 7,
                                                }}
                                            />

                                            <div
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 10,
                                                    marginBottom: 15,
                                                }}
                                            >
                                                <BusinessIcon />

                                                <Typography
                                                    style={{
                                                        fontWeight: 700,
                                                        fontSize: 18,
                                                        fontFamily: 'Inter, Roboto, Helvetica, Arial, sans-serif',
                                                    }}
                                                >
                                                    Requirements
                                                </Typography>
                                            </div>

                                            <TableContainer
                                                component={Paper}
                                                style={{
                                                    borderRadius: 12,
                                                    overflow: "hidden",
                                                }}
                                            >

                                                <Table>

                                                    <TableHead>

                                                        <TableRow
                                                            style={{
                                                                background: "#F3F4F6",
                                                            }}
                                                        >
                                                            <TableCell className="table-header-cell-style">
                                                                Employer Unit
                                                            </TableCell>

                                                            <TableCell className="table-header-cell-style">
                                                                Department
                                                            </TableCell>

                                                            <TableCell className="table-header-cell-style">
                                                                Opening
                                                            </TableCell>

                                                            <TableCell className="table-header-cell-style">
                                                                Role
                                                            </TableCell>

                                                            <TableCell className="table-header-cell-style">
                                                                CTC
                                                            </TableCell>

                                                            <TableCell className="table-header-cell-style">
                                                                In Hand
                                                            </TableCell>
                                                        </TableRow>

                                                    </TableHead>

                                                    <TableBody>

                                                        {
                                                            item.requirements?.map(
                                                                (req, reqIndex) => (

                                                                    <TableRow
                                                                        key={reqIndex}
                                                                    >

                                                                        <TableCell className="table-body-cell-style">
                                                                            <div
                                                                                style={{
                                                                                    display: "flex",
                                                                                    alignItems: "center",
                                                                                    gap: 8,
                                                                                }}
                                                                            >
                                                                                <WorkIcon
                                                                                    style={{
                                                                                        fontSize: 16,
                                                                                        color: "#6B7280",
                                                                                    }}
                                                                                />

                                                                                {
                                                                                    req.employer_unit || "-"
                                                                                }
                                                                            </div>
                                                                        </TableCell>

                                                                        <TableCell className="table-body-cell-style">
                                                                            {req.department || "-"}
                                                                        </TableCell>

                                                                        <TableCell className="table-body-cell-style">
                                                                            <Chip
                                                                                label={`${req.opening || 0} Openings`}
                                                                                size="small"
                                                                                color="primary"
                                                                            />
                                                                        </TableCell>

                                                                        <TableCell className="table-body-cell-style">
                                                                            {req.role || "-"}
                                                                        </TableCell>

                                                                        <TableCell className="table-body-cell-style">
                                                                            ₹ {req.CTC || 0}
                                                                        </TableCell>

                                                                        <TableCell className="table-body-cell-style">
                                                                            ₹ {req.in_hand || 0}
                                                                        </TableCell>

                                                                    </TableRow>
                                                                )
                                                            )
                                                        }
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                            <hr />
                                            {/* BENEFITS */}
                                            <Typography
                                                style={{
                                                    fontWeight: 700,
                                                    fontSize: 17,
                                                    marginBottom: 16,
                                                    color: "#111827",
                                                }}
                                            >
                                                Benefits & Facilities
                                            </Typography>

                                            <Box
                                                style={{
                                                    display: 'flex',
                                                    flexWrap: 'wrap',
                                                    gap: 16,
                                                    justifyContent: 'space-between',
                                                }}
                                            >
                                                {[{
                                                    title: 'Accommodation',
                                                    value: item.accommodation || '-',
                                                    extra: `Type: ${item.accommodation_type || '-'}`,
                                                    extra2: `Deduction: ${item.accommodation_deduction || '-'}`,
                                                }, {
                                                    title: 'Meals',
                                                    value: item.meals || '-',
                                                }, {
                                                    title: 'Service Charge',
                                                    value: item.service_charge || '-',
                                                }, {
                                                    title: 'Relocation',
                                                    value: item.relocation || '-',
                                                    extra: item.relocation_remark ? `Remark: ${item.relocation_remark}` : null,
                                                }, {
                                                    title: 'Other Notes',
                                                    value: item.other_notes || '-',
                                                }].map((card, cardIndex) => (
                                                    <Card
                                                        key={cardIndex}
                                                        style={{
                                                            padding: 16,
                                                            minHeight: 150,
                                                            flex: '1 1 calc(20% - 16px)',
                                                            minWidth: 180,
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            justifyContent: 'space-between',
                                                        }}
                                                    >
                                                        <Typography
                                                            style={{
                                                                color: '#6B7280',
                                                                fontSize: 13,
                                                                fontFamily: 'Inter, Roboto, Helvetica, Arial, sans-serif',
                                                            }}
                                                        >
                                                            {card.title}
                                                        </Typography>

                                                        <Typography
                                                            style={{
                                                                fontWeight: 600,
                                                                marginTop: 5,
                                                                fontFamily: 'Inter, Roboto, Helvetica, Arial, sans-serif',
                                                            }}
                                                        >
                                                            {card.value}
                                                        </Typography>

                                                        {card.extra && (
                                                            <Typography
                                                                style={{
                                                                    fontSize: 13,
                                                                    color: '#6B7280',
                                                                    marginTop: 5,
                                                                    fontFamily: 'Inter, Roboto, Helvetica, Arial, sans-serif',
                                                                }}
                                                            >
                                                                {card.extra}
                                                            </Typography>
                                                        )}
                                                        {card.extra2 && (
                                                            <Typography
                                                                style={{
                                                                    fontSize: 13,
                                                                    color: '#6B7280',
                                                                    fontFamily: 'Inter, Roboto, Helvetica, Arial, sans-serif',
                                                                }}
                                                            >
                                                                {card.extra2}
                                                            </Typography>
                                                        )}
                                                    </Card>
                                                ))}
                                            </Box>

                                            {/* PLACEMENT DETAILS */}
                                            {item.placementDetails && item.placementDetails.length > 0 && (
                                                <>
                                                    <Divider
                                                        className="divider-placement-details"
                                                    />

                                                    <Typography
                                                        className="typography-placement-details"
                                                    >
                                                        Placement Details
                                                    </Typography>

                                                    <TableContainer component={Paper}>
                                                        <Table>
                                                            <TableHead>
                                                                <TableRow className="placement-tab-row">
                                                                    <TableCell className="table-header-cell-style">Project Name</TableCell>
                                                                    <TableCell className="table-header-cell-style">Candidate Name</TableCell>
                                                                    <TableCell className="table-header-cell-style">Training Partner</TableCell>
                                                                    <TableCell className="table-header-cell-style">Mandate Placement Unit</TableCell>
                                                                    <TableCell className="table-header-cell-style">Placement Date</TableCell>
                                                                </TableRow>
                                                            </TableHead>
                                                            <TableBody>
                                                                {item.placementDetails.map((placement, placementIndex) => (
                                                                    <TableRow key={placementIndex}>
                                                                        <TableCell className="table-body-cell-style">
                                                                            {placement.programName || '-'}
                                                                        </TableCell>
                                                                        <TableCell className="table-body-cell-style">
                                                                            {placement.candidateName || '-'}
                                                                        </TableCell>
                                                                        <TableCell className="table-body-cell-style">
                                                                            {placement.candidateTrainingPartner || '-'}
                                                                        </TableCell>
                                                                        <TableCell className="table-body-cell-style">
                                                                            {placement.placementUnit || '-'}
                                                                        </TableCell>
                                                                        <TableCell className="table-body-cell-style">
                                                                            {placement.placementDate || '-'}
                                                                        </TableCell>
                                                                    </TableRow>
                                                                ))}
                                                            </TableBody>
                                                        </Table>
                                                    </TableContainer>
                                                </>
                                            )}
                                        </AccordionDetails>
                                    </Accordion>
                                ))
                            )
                        }

                        {mandateData.length > 0 && (
                            <TablePagination
                                component="div"
                                count={mandateData.length}
                                page={page}
                                onPageChange={handleChangePage}
                                rowsPerPage={rowsPerPage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                rowsPerPageOptions={[5, 10, 15, 20]}
                            />
                        )}

                    </div>
                )
            }
        </>
    );
};

export default EmployerMandateList;